import React from 'react';
import moment from 'moment-timezone';
import {Dropdown, Text, Icon, ListGroupItem, Button, Label, Input, Field} from '@albert-io/atomic';
import {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';
import {Map} from 'immutable';
import {ClassroomAssignmentSettings} from './RecipientSettings.types';
import {handleDateChange, validDateRange, validTimeRange} from '../settingsModal.utils';

import './classroom-table.scss';

interface DueDateDropdownProps {
  classroomObj: ClassroomModelV1;
  selectedClassrooms: Map<string, ClassroomAssignmentSettings>;
  setSelectedClassrooms: (selectedClassrooms: Map<string, ClassroomAssignmentSettings>) => void;
}

const DueDateDropdown = ({
  classroomObj,
  selectedClassrooms,
  setSelectedClassrooms
}: DueDateDropdownProps) => {
  const classroomId = classroomObj.classroomInfo.getId();
  const hasDateError = !validDateRange(classroomObj.startDate, classroomObj.dueDate);
  const hasTimeError = !validTimeRange(
    classroomObj.startDate,
    classroomObj.dueDate,
    classroomObj.startDateTime,
    classroomObj.dueDateTime
  );

  return (
    <Dropdown
      trigger={
        <button
          type='button'
          className='classroom-table__dropdown-button'
          data-test-id='AB-settings-modal__due-date-dropdown'
        >
          {classroomObj.dueDate && classroomObj.dueDateTime && !hasDateError && !hasTimeError ? (
            <div className='u-display_flex u-justify-content_space-between u-width_100pc u-align-items_center'>
              <div
                style={{gap: '0.3em'}}
                className='u-display_flex u-flex-direction_column u-text-align_left'
              >
                <Text size='m'>
                  {moment(`${classroomObj.dueDate} ${classroomObj.dueDateTime} `).format(
                    'MMM DD, YYYY'
                  )}
                </Text>
                <Text size='m' emphasis='lowest'>
                  {`${moment(`${classroomObj.dueDate} ${classroomObj.dueDateTime} `).format(
                    'hh:mm a'
                  )} ${moment.tz(moment.tz.guess()).zoneAbbr()}`}
                </Text>
              </div>
              <Icon icon='caret-down' color='midnight-lightest' className='u-mar-r_1' />
            </div>
          ) : (
            <div className='u-display_flex u-justify-content_space-between u-width_100pc u-align-items_center'>
              <Text size='m' italic emphasis='lowest'>
                Choose...
              </Text>
              <Icon icon='caret-down' color='midnight-lightest' className='u-mar-r_1' />
            </div>
          )}
        </button>
      }
      className='classroom-table__dropdown'
    >
      <ListGroupItem onClick={(e) => e.stopPropagation()}>
        <div className='u-mar-b_2'>
          <Label>
            <Field
              data-test-id='AB-settings-modal__due-date-field'
              as={Input}
              label='Day'
              type='date'
              message={hasDateError && 'Due date must be after start date.'}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                handleDateChange(
                  classroomObj,
                  {dueDate: e.target.value},
                  selectedClassrooms,
                  setSelectedClassrooms,
                  classroomId
                );
              }}
              error={hasDateError}
              value={classroomObj.dueDate}
            />
          </Label>
        </div>
        <div>
          <Label htmlFor='due-date-time'>
            <Field
              as={Input}
              label='Time'
              type='time'
              message={hasTimeError && 'Due date time must be after start date time.'}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                handleDateChange(
                  classroomObj,
                  {dueDateTime: e.target.value},
                  selectedClassrooms,
                  setSelectedClassrooms,
                  classroomId
                );
              }}
              error={hasTimeError}
              value={classroomObj.dueDate ? classroomObj.dueDateTime : ''}
            />
          </Label>
        </div>
      </ListGroupItem>
      <div className='u-text-align_right u-mar-tb_1 u-mar-r_1'>
        <Button size='s' variant='text' disabled={hasDateError || hasTimeError}>
          Done
        </Button>
      </div>
    </Dropdown>
  );
};

export default DueDateDropdown;
